import { useCallback, useEffect, useRef, useState } from "react";

type ToastKind = "success" | "error" | "info";

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
}

const TOAST_EVENT = "cymergold-toast";
const AUTO_DISMISS_MS = 4_500;
const DEDUPE_WINDOW_MS = 2_500;

function toastKindFromElement(element: Element): ToastKind {
  if (element.classList.contains("form-error") || element.classList.contains("alert--error")) {
    return "error";
  }
  if (element.classList.contains("alert--success") || element.classList.contains("form-message")) {
    return "success";
  }
  return "info";
}

export function collectVisibleMessages(root: ParentNode): Array<{ message: string; kind: ToastKind }> {
  // Errors are included so feedback surfaces consistently: long pages keep
  // the inline message where the action happened AND get a visible toast.
  const candidates = root.querySelectorAll(
    ".form-message, .alert--success, .form-error, .alert--error",
  );
  return Array.from(candidates)
    .filter((element) => !element.closest(".toast-stack"))
    // Regla de Eduardo (2026-09-18/20): un toast es la respuesta a una ACCIÓN.
    // Los avisos que describen el estado de la pantalla (compuerta del wizard,
    // "esta categoría juega en un solo grupo"…) llevan data-no-toast y se
    // quedan dentro de la página: no deben saltar al entrar a una sección.
    .filter((element) => !element.closest("[data-no-toast]"))
    .map((element) => ({
      message: element.textContent?.trim() ?? "",
      kind: toastKindFromElement(element),
    }))
    .filter((item) => item.message.length > 0);
}

export function showGlobalToast(message: string, kind: ToastKind = "success") {
  if (typeof window === "undefined" || !message.trim()) return;
  window.dispatchEvent(
    new CustomEvent(TOAST_EVENT, {
      detail: { message: message.trim(), kind },
    }),
  );
}

export default function GlobalToasts() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextIdRef = useRef(1);
  const lastShownRef = useRef<{ key: string; at: number } | null>(null);

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const pushToast = useCallback((message: string, kind: ToastKind = "success") => {
    const cleanMessage = message.trim();
    if (!cleanMessage) return;

    const now = Date.now();
    const key = `${kind}:${cleanMessage}`;
    if (lastShownRef.current?.key === key && now - lastShownRef.current.at < DEDUPE_WINDOW_MS) {
      return;
    }
    lastShownRef.current = { key, at: now };

    const id = nextIdRef.current++;
    setToasts((current) => [...current.slice(-2), { id, message: cleanMessage, kind }]);
    window.setTimeout(() => dismissToast(id), AUTO_DISMISS_MS);
  }, [dismissToast]);

  useEffect(() => {
    function handleToastEvent(event: Event) {
      const detail = (event as CustomEvent<{ message?: string; kind?: ToastKind }>).detail;
      if (!detail?.message) return;
      pushToast(detail.message, detail.kind ?? "success");
    }

    window.addEventListener(TOAST_EVENT, handleToastEvent);
    return () => window.removeEventListener(TOAST_EVENT, handleToastEvent);
  }, [pushToast]);

  useEffect(() => {
    const main = document.querySelector(".app-main") ?? document.body;
    let checkQueued = false;

    const checkMessages = () => {
      checkQueued = false;
      collectVisibleMessages(main).forEach((item) => pushToast(item.message, item.kind));
    };

    const queueCheck = () => {
      if (checkQueued) return;
      checkQueued = true;
      window.requestAnimationFrame(checkMessages);
    };

    const observer = new MutationObserver(queueCheck);
    observer.observe(main, { childList: true, characterData: true, subtree: true });
    checkMessages();

    return () => observer.disconnect();
  }, [pushToast]);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-stack" role="status" aria-live="polite" aria-atomic="false">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast--${toast.kind}`}>
          <span className="toast__icon" aria-hidden="true">
            {toast.kind === "success" ? "✓" : toast.kind === "error" ? "!" : "i"}
          </span>
          <p>{toast.message}</p>
          <button type="button" onClick={() => dismissToast(toast.id)} aria-label="Cerrar aviso">
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
